import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { message, Row, Col } from 'antd';

import FormSectionTitle from '../../FormSectionTitle';
import EmptyState from '../../EmptyState';
import PreviewProfileItem from '../../PreviewProfileItem';
import CustomModal from '../../../atoms/CustomModal';
import DeleteModal from '../../../atoms/DeleteModal';
import LocationForm from './LocationForm';

import { triggerChangePercentage } from '../../../../store/slices/sellerProfileSlice';
import {
  useGetProfileBranchQuery,
  useGetProfileWarehouseQuery,
  useDeleteProfileBranchMutation,
  useDeleteProfileWarehouseMutation,
} from '../../../../store/services/sellerProfileCreationService';

export default function BranchesAndWarehouses() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const [modalType, setModalType] = useState<'BRANCH' | 'WAREHOUSE' | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<'BRANCH' | 'WAREHOUSE' | null>(null);

  const { sellerId } = useParams<{ sellerId: string }>();
  const profileId = sellerId || '';

  const { data: branches, refetch: refetchBranches, isLoading: isBranchesLoading } = useGetProfileBranchQuery({ profileId });
  const { data: warehouses, refetch: refetchWarehouses, isLoading: isWarehousesLoading } = useGetProfileWarehouseQuery({ profileId });

  const [deleteBranch, { isLoading: isDeleteBranchLoading }] = useDeleteProfileBranchMutation();
  const [deleteWarehouse, { isLoading: isDeleteWarehouseLoading }] = useDeleteProfileWarehouseMutation();

  const handleDelete = async () => {
    if (deleteId && deleteType) {
      try {
        if (deleteType === 'BRANCH') {
          await deleteBranch({ branchId: deleteId });
          refetchBranches();
        } else {
          await deleteWarehouse({ warehouseId: deleteId });
          refetchWarehouses();
        }
        dispatch(triggerChangePercentage());
        setDeleteModalOpen(false);
      } catch (err) {
        message.error(t('components.somethingWentWrong'));
      }
    }
  };

  const handleOpenModal = (type: 'BRANCH' | 'WAREHOUSE', data: any = null) => {
    setModalType(type);
    setModalData(data);
    setModalOpen(true);
  };

  return (
    <div className="flex flex-col gap-12 pb-3" data-testid="branches-warehouses-container">
      {/* Branches Section */}
      <div className="flex flex-col" data-testid="branches-section">
        <FormSectionTitle
          title={t('components.branches_section_title')}
          subTitle={t('components.branches_desc')}
          buttonText={t('components.add_branches')}
          buttonId="add-branch-btn"
          onClick={() => handleOpenModal('BRANCH')}
        />

        {isBranchesLoading ? (
          <p data-testid="branches-loading">{t('components.loading')}</p>
        ) : branches?.payload?.length ? (
          <Row gutter={[24, 24]}>
            {branches.payload.map((loc: any) => (
              <Col xs={24} key={loc.id}>
                <PreviewProfileItem
                  itemId={loc.id}
                  title={loc.name}
                  description={loc.address}
                  link={loc.lat && loc.lang ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lang}` : ''}
                  isEditable
                  isDelete
                  onEdit={() => handleOpenModal('BRANCH', loc)}
                  onDelete={() => {
                    setDeleteId(loc.id);
                    setDeleteType('BRANCH');
                    setDeleteModalOpen(true);
                  }}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <EmptyState onAdd={() => handleOpenModal('BRANCH')} />
        )}
      </div>

      {/* Warehouses Section */}
      <div className="flex flex-col" data-testid="warehouses-section">
        <FormSectionTitle
          title={t('components.warehouses')}
          subTitle={t('components.warehouses_desc')}
          buttonText={t('components.add_warehouse')}
          buttonId="add-warehouse-btn"
          onClick={() => handleOpenModal('WAREHOUSE')}
        />

        {isWarehousesLoading ? (
          <p data-testid="warehouses-loading">{t('components.loading')}</p>
        ) : warehouses?.payload?.length ? (
          <Row gutter={[24, 24]}>
            {warehouses.payload.map((loc: any) => (
              <Col xs={24} key={loc.id}>
                <PreviewProfileItem
                  itemId={loc.id}
                  title={loc.name}
                  description={loc.address}
                  link={loc.lat && loc.lang ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lang}` : ''}
                  isEditable
                  isDelete
                  onEdit={() => handleOpenModal('WAREHOUSE', loc)}
                  onDelete={() => {
                    setDeleteId(loc.id);
                    setDeleteType('WAREHOUSE');
                    setDeleteModalOpen(true);
                  }}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <EmptyState onAdd={() => handleOpenModal('WAREHOUSE')} />
        )}
      </div>

      <CustomModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={modalType === 'BRANCH' ? t('components.add_branch') : t('components.add_warehouse')}
        description={modalType === 'BRANCH' ? t('components.add_branch_desc') : t('components.add_warehouse_desc')}
        variant="supplier"
        withoutLogo={false}
        centered
      >
        {modalOpen && modalType && (
          <LocationForm
            profileId={profileId}
            initData={modalData}
            type={modalType}
            onSuccess={() => {
              setModalOpen(false);
              if (modalType === 'BRANCH') refetchBranches();
              else refetchWarehouses();
              dispatch(triggerChangePercentage());
            }}
          />
        )}
      </CustomModal>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        isLoading={isDeleteBranchLoading || isDeleteWarehouseLoading}
        title={t(deleteType === 'BRANCH' ? 'components.deleteBranchTitle' : 'components.deleteWarehouseTitle')}
        description={t(deleteType === 'BRANCH' ? 'components.deleteBranchDescription' : 'components.deleteWarehouseDescription')}
      />
    </div>
  );
}
